import React, { useState, useEffect } from 'react';
import { Row, Col, Card, CardBody, CardHeader, Table, Nav, NavItem, NavLink, TabContent, TabPane } from 'reactstrap';
import classnames from 'classnames';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Orange color palette variations
const ORANGE_COLORS = {
  primary: '#FF5B00',        // Main orange
  dark: '#e85a28',           // Dark orange
  darker: '#d14d1f',         // Darker orange
  light: '#ff8c61',          // Light orange
  lighter: '#ffb399',        // Lighter orange
  subtle: '#fff5f0',         // Very light orange background
  muted: '#ffe8dd',          // Muted orange
};

const detailCardShell = {
  background: "var(--vz-secondary-bg, #fff)",
  border: "1px solid var(--vz-border-color)",
  borderRadius: 10,
  boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  overflow: "hidden",
};

function Office365Details({ threat }) {
  const [activeTab, setActiveTab] = useState('1');
  const [complianceData, setComplianceData] = useState([]);
  const [mitreData, setMitreData] = useState([]);
  const [loading, setLoading] = useState(true);

  const toggle = tab => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  const getAuthHeaders = () => {
    const authUser = JSON.parse(sessionStorage.getItem("authUser"));
    return {
      "Content-Type": "application/json",
      ...(authUser?.token && { Authorization: `Bearer ${authUser.token}` }),
    };
  };

  useEffect(() => {
    const fetchAdditionalData = async () => {
      if (!threat || !threat.threat_id) {
        setLoading(false);
        return;
      }

      try {
        const headers = getAuthHeaders();
        const response = await fetch(`${API_BASE_URL}/threats/${threat.threat_id}`, { headers });
        const data = await response.json();

        if (data.success && data.threat) {
          // Process compliance data from the new structure
          if (data.threat.compliance_frameworks && data.threat.compliance_controls) {
            const compliance = data.threat.compliance_frameworks.map((framework, idx) => ({
              framework: framework,
              control_id: data.threat.compliance_controls[idx] || 'N/A'
            }));
            setComplianceData(compliance);
          }

          // Process MITRE data from the new structure
          if (data.threat.mitre_tactics && data.threat.mitre_techniques) {
            const mitre = data.threat.mitre_tactics.map((tactic, idx) => ({
              tactic: tactic,
              technique: data.threat.mitre_techniques[idx] || 'N/A',
              technique_id: data.threat.mitre_techniques[idx] || 'N/A'
            }));
            setMitreData(mitre);
          }
        }
      } catch (error) {
        console.error("Error fetching additional data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAdditionalData();
  }, [threat]);

  // Also process compliance data from props if available
  useEffect(() => {
    if (threat) {
      if (threat.compliance_frameworks && threat.compliance_controls) {
        const compliance = threat.compliance_frameworks.map((framework, idx) => ({
          framework: framework,
          control_id: threat.compliance_controls[idx] || 'N/A'
        }));
        setComplianceData(compliance);
      }

      if (threat.mitre_tactics && threat.mitre_techniques) {
        const mitre = threat.mitre_tactics.map((tactic, idx) => ({
          tactic: tactic,
          technique: threat.mitre_techniques[idx] || 'N/A',
          technique_id: threat.mitre_techniques[idx] || 'N/A'
        }));
        setMitreData(mitre);
      }
    }
  }, [threat]);

  if (!threat) return null;

  // Helper function to get badge class based on criticality
  const getCriticalityBadgeClass = (criticality) => {
    if (!criticality) return 'orange-badge-subtle';
    const level = criticality.toLowerCase();
    if (level === 'critical') return 'badge-critical';
    if (level === 'high') return 'badge-high';
    if (level === 'medium') return 'badge-medium';
    if (level === 'low') return 'badge-low';
    return 'orange-badge-subtle';
  };

  // Add this function to sanitize the threat object
const getSanitizedThreat = (threat) => {
  if (!threat) return null;
  
  const sanitized = { ...threat };
  
  // Rename wazuh fields to generic names
  if (sanitized.wazuh_rule_id !== undefined) {
    sanitized.rule_id = sanitized.wazuh_rule_id;
    delete sanitized.wazuh_rule_id;
  }
  
  if (sanitized.wazuh_rule_level !== undefined) {
    sanitized.rule_level = sanitized.wazuh_rule_level;
    delete sanitized.wazuh_rule_level;
  }
  
  if (sanitized.wazuh_rule_description !== undefined) {
    sanitized.rule_description = sanitized.wazuh_rule_description;
    delete sanitized.wazuh_rule_description;
  }
  
  if (sanitized.wazuh_rule_groups !== undefined) {
    sanitized.rule_groups = sanitized.wazuh_rule_groups;
    delete sanitized.wazuh_rule_groups;
  }
  
  return sanitized;
};

  return (
    <>
      <style>
        {`
          .orange-card-header {
            background-color: ${ORANGE_COLORS.primary} !important;
            color: white !important;
          }
          .orange-card-header-dark {
            background-color: ${ORANGE_COLORS.dark} !important;
            color: white !important;
          }
          .orange-card-header-darker {
            background-color: ${ORANGE_COLORS.darker} !important;
            color: white !important;
          }
          .orange-badge-subtle {
            background-color: rgba(255, 107, 53, 0.1) !important;
            color: ${ORANGE_COLORS.primary} !important;
            border: none !important;
            padding: 0.35rem 0.75rem;
            border-radius: 50rem;
            font-size: 0.75rem;
            font-weight: 500;
          }
          .badge-critical {
            background-color: rgba(139, 0, 0, 0.1) !important;
            color: #8b0000 !important;
            border: none !important;
            padding: 0.35rem 0.75rem;
            border-radius: 50rem;
            font-size: 0.75rem;
            font-weight: 500;
          }
          .badge-high {
            background-color: rgba(220, 53, 69, 0.1) !important;
            color: #dc3545 !important;
            border: none !important;
            padding: 0.35rem 0.75rem;
            border-radius: 50rem;
            font-size: 0.75rem;
            font-weight: 500;
          }
          .badge-medium {
            background-color: rgba(255, 193, 7, 0.1) !important;
            color: #ffc107 !important;
            border: none !important;
            padding: 0.35rem 0.75rem;
            border-radius: 50rem;
            font-size: 0.75rem;
            font-weight: 500;
          }
          .badge-low {
            background-color: rgba(25, 135, 84, 0.1) !important;
            color: #198754 !important;
            border: none !important;
            padding: 0.35rem 0.75rem;
            border-radius: 50rem;
            font-size: 0.75rem;
            font-weight: 500;
          }
          .orange-avatar-bg {
            background-color: rgba(255, 107, 53, 0.1) !important;
            color: ${ORANGE_COLORS.primary} !important;
          }
          .orange-avatar-solid {
            background-color: ${ORANGE_COLORS.primary} !important;
            color: white !important;
          }
          .orange-avatar-dark {
            background-color: ${ORANGE_COLORS.dark} !important;
            color: white !important;
          }
          .orange-avatar-light {
            background-color: ${ORANGE_COLORS.light} !important;
            color: white !important;
          }
          .orange-button {
            background-color: transparent !important;
            border: 1px solid white !important;
            color: white !important;
          }
          .orange-button:hover {
            background-color: rgba(255, 255, 255, 0.1) !important;
          }
          .orange-text {
            color: ${ORANGE_COLORS.primary} !important;
          }
        `}
      </style>

      {/* Tab Navigation */}
      <Row className="mb-3">
        <Col>
          <Card className="ribbon-box ">
            <CardBody className="p-0">
              <Nav tabs className="nav-tabs-custom nav-justified mb-0">
                <NavItem>
                  <NavLink
                    style={{ cursor: "pointer" }}
                    className={classnames({ active: activeTab === '1' })}
                    onClick={() => { toggle('1'); }}
                  >
                    <i className="ri-cloud-line me-1"></i>
                    Activity
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    style={{ cursor: "pointer" }}
                    className={classnames({ active: activeTab === '2' })}
                    onClick={() => { toggle('2'); }}
                  >
                    <i className="ri-shield-check-line me-1"></i>
                    Security
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    style={{ cursor: "pointer" }}
                    className={classnames({ active: activeTab === '3' })}
                    onClick={() => { toggle('3'); }}
                  >
                    <i className="ri-settings-3-line me-1"></i>
                    Technical
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    style={{ cursor: "pointer" }}
                    className={classnames({ active: activeTab === '4' })}
                    onClick={() => { toggle('4'); }}
                  >
                    <i className="ri-file-code-line me-1"></i>
                    Raw Data
                  </NavLink>
                </NavItem>
              </Nav>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Tab Content */}
      <TabContent activeTab={activeTab}>
        {/* TAB 1: Activity Details */}
        <TabPane tabId="1">
          {/* User Activity Cards - showing data from JSON */}
          <Row className="mb-3">
            <Col xxl={4}>
              <Card style={detailCardShell}>
                <CardHeader className="orange-card-header">
                  <h6 className="card-title mb-0 text-white">
                    <i className="ri-user-3-line me-2"></i>
                    User Information
                  </h6>
                </CardHeader>
                <CardBody>
                  <div className="text-center mb-3">
                    <div className="avatar-lg mx-auto mb-3">
                      <div className="avatar-title orange-avatar-bg rounded-circle fs-20">
                        <i className="ri-user-line"></i>
                      </div>
                    </div>
                    <h5 className="mb-1">{threat.asset_name || 'Unknown User'}</h5>
                    <p className="text-muted mb-0">{threat.agent_name || 'Office 365'}</p>
                  </div>
                  
                  <div className="table-responsive">
                    <Table className="table-borderless mb-0">
                      <tbody>
                        <tr>
                          <td className="text-muted">Asset Name:</td>
                          <td className="text-end">
                            <code className="small">{threat.asset_name || 'N/A'}</code>
                          </td>
                        </tr>
                        <tr>
                          <td className="text-muted">Threat Type:</td>
                          <td className="text-end">
                            <span className="orange-badge-subtle">
                              {threat.threat_type || 'N/A'}
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <td className="text-muted">Agent:</td>
                          <td className="text-end">
                            <span className="orange-badge-subtle">
                              {threat.agent_name || 'N/A'}
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <td className="text-muted">Source IP:</td>
                          <td className="text-end">
                            <code className="small">{threat.source_ip || 'N/A'}</code>
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col xxl={4}>
              <Card style={detailCardShell}>
                <CardHeader className="orange-card-header-dark">
                  <h6 className="card-title mb-0 text-white">
                    <i className="ri-file-list-line me-2"></i>
                    Threat Details
                  </h6>
                </CardHeader>
                <CardBody>
                  <div className="text-center mb-3">
                    <div className="avatar-lg mx-auto mb-3">
                      <div className="avatar-title orange-avatar-bg rounded-circle fs-20">
                        <i className="ri-alert-line"></i>
                      </div>
                    </div>
                    <h6 className="mb-2">{threat.threat_description || 'No description available'}</h6>
                    <p className="text-muted small mb-0">Threat Description</p>
                  </div>

                  <div className="table-responsive">
                    <Table className="table-borderless mb-0">
                      <tbody>
                        <tr>
                          <td className="text-muted">Threat ID:</td>
                          <td className="text-end">
                            <code className="small">{threat.threat_id || 'N/A'}</code>
                          </td>
                        </tr>
                        <tr>
                          <td className="text-muted">Status:</td>
                          <td className="text-end">
                            <span className="orange-badge-subtle">
                              {threat.status || 'Active'}
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <td className="text-muted">Criticality:</td>
                          <td className="text-end">
                            <span className={getCriticalityBadgeClass(threat.asset_criticality)}>
                              {threat.asset_criticality || 'Low'}
                            </span>
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col xxl={4}>
              <Card style={detailCardShell}>
                <CardHeader className="orange-card-header-darker">
                  <h6 className="card-title mb-0 text-white">
                    <i className="ri-time-line me-2"></i>
                    Timestamp Information
                  </h6>
                </CardHeader>
                <CardBody>
                  <div className="text-center mb-3">
                    <div className="avatar-lg mx-auto mb-3">
                      <div className="avatar-title orange-avatar-bg rounded-circle fs-20">
                        <i className="ri-calendar-line"></i>
                      </div>
                    </div>
                    <h5 className="mb-1">
                      {threat.timestamp ? new Date(threat.timestamp).toLocaleDateString() : 'Unknown'}
                    </h5>
                    <p className="text-muted mb-0">Detection Date</p>
                  </div>
                  
                  <div className="table-responsive">
                    <Table className="table-borderless mb-0">
                      <tbody>
                        <tr>
                          <td className="text-muted">Detected:</td>
                          <td className="text-end">
                            <small>{threat.timestamp ? new Date(threat.timestamp).toLocaleString() : 'N/A'}</small>
                          </td>
                        </tr>
                        <tr>
                          <td className="text-muted">Created:</td>
                          <td className="text-end">
                            <small>{threat.created_at ? new Date(threat.created_at).toLocaleString() : 'N/A'}</small>
                          </td>
                        </tr>
                        <tr>
                          <td className="text-muted">Updated:</td>
                          <td className="text-end">
                            <small>{threat.updated_at ? new Date(threat.updated_at).toLocaleString() : 'N/A'}</small>
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Activity Timeline */}
          <Row className="mb-3">
            <Col xl={12}>
              <Card className="">
                <CardHeader className="orange-card-header">
                  <h5 className="card-title mb-0 text-white">
                    <i className="ri-time-line me-2"></i>
                    Activity Timeline
                  </h5>
                </CardHeader>
                <CardBody>
                  <div className="profile-timeline">
                    <div className="accordion accordion-flush" id="accordionFlushExample">
                      {/* Threat Detection */}
                      <div className="accordion-item border-0">
                        <div className="accordion-header">
                          <button 
                            className="accordion-button p-2 shadow-none" 
                            type="button"
                            style={{ cursor: 'default' }}
                          >
                            <div className="d-flex align-items-center">
                              <div className="flex-shrink-0 avatar-xs">
                                <div className="avatar-title orange-avatar-solid rounded-circle">
                                  <i className="ri-alert-line"></i>
                                </div>
                              </div>
                              <div className="flex-grow-1 ms-3">
                                <h6 className="fs-14 mb-1">{threat.threat_type || 'Threat'} - {threat.threat_description}</h6>
                                <small className="text-muted">
                                  {threat.asset_name || 'Unknown User'} · {threat.timestamp ? new Date(threat.timestamp).toLocaleString() : 'N/A'}
                                </small>
                              </div>
                            </div>
                          </button>
                        </div>
                      </div>

                      {/* Source Information */}
                      <div className="accordion-item border-0">
                        <div className="accordion-header">
                          <button 
                            className="accordion-button p-2 shadow-none" 
                            type="button"
                            style={{ cursor: 'default' }}
                          >
                            <div className="d-flex align-items-center">
                              <div className="flex-shrink-0 avatar-xs">
                                <div className="avatar-title orange-avatar-dark rounded-circle">
                                  <i className="ri-global-line"></i>
                                </div>
                              </div>
                              <div className="flex-grow-1 ms-3">
                                <h6 className="fs-14 mb-1">Source IP: {threat.source_ip || 'Unknown'}</h6>
                                <small className="text-muted">
                                  {threat.agent_name} · Criticality: {threat.asset_criticality}
                                </small>
                              </div>
                            </div>
                          </button>
                        </div>
                      </div>

                      {/* Alert Triggered */}
                      <div className="accordion-item border-0">
                        <div className="accordion-header">
                          <button 
                            className="accordion-button p-2 shadow-none" 
                            type="button"
                            style={{ cursor: 'default' }}
                          >
                            <div className="d-flex align-items-center">
                              <div className="flex-shrink-0 avatar-xs">
                                <div className="avatar-title orange-avatar-light rounded-circle">
                                  <i className="ri-alarm-warning-line"></i>
                                </div>
                              </div>
                              <div className="flex-grow-1 ms-3">
                                <h6 className="fs-14 mb-1">Security Alert Triggered</h6>
                                <small className="text-muted">
                                  Rule {threat.wazuh_rule_id} · Severity Level {threat.wazuh_rule_level}
                                </small>
                              </div>
                            </div>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Activity Indicators */}
          <Row>
            <Col xl={12}>
              <Card style={detailCardShell}>
                <CardHeader className="orange-card-header">
                  <h5 className="card-title mb-0 text-white">
                    <i className="ri-radar-line me-2"></i>
                    Activity Indicators
                  </h5>
                </CardHeader>
                <CardBody className="p-0">
                  <div className="table-responsive">
                    <Table className="align-middle table-nowrap mb-0">
                      <thead className="table-light">
                        <tr>
                          <th scope="col">Indicator Type</th>
                          <th scope="col">Value</th>
                          <th scope="col">Context</th>
                          <th scope="col">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>
                            <div className="d-flex align-items-center">
                              <div className="flex-shrink-0 avatar-xs">
                                <div className="avatar-title orange-avatar-bg rounded-circle">
                                  <i className="ri-global-line"></i>
                                </div>
                              </div>
                              <div className="flex-grow-1 ms-2">
                                IP Address
                              </div>
                            </div>
                          </td>
                          <td><code>{threat.source_ip || 'N/A'}</code></td>
                          <td>Source Access Location</td>
                          <td>
                            <span className={getCriticalityBadgeClass(threat.asset_criticality)}>
                              {threat.asset_criticality || 'Low'}
                            </span>
                          </td>
                        </tr>

                        <tr>
                          <td>
                            <div className="d-flex align-items-center">
                              <div className="flex-shrink-0 avatar-xs">
                                <div className="avatar-title orange-avatar-bg rounded-circle">
                                  <i className="ri-user-line"></i>
                                </div>
                              </div>
                              <div className="flex-grow-1 ms-2">
                                Asset Name
                              </div>
                            </div>
                          </td>
                          <td><strong>{threat.asset_name || 'Unknown'}</strong></td>
                          <td>Office 365 Asset</td>
                          <td>
                            <span className="orange-badge-subtle">Monitored</span>
                          </td>
                        </tr>

                        <tr>
                          <td>
                            <div className="d-flex align-items-center">
                              <div className="flex-shrink-0 avatar-xs">
                                <div className="avatar-title orange-avatar-bg rounded-circle">
                                  <i className="ri-shield-line"></i>
                                </div>
                              </div>
                              <div className="flex-grow-1 ms-2">
                                Threat Type
                              </div>
                            </div>
                          </td>
                          <td><code className="small">{threat.threat_type || 'N/A'}</code></td>
                          <td>{threat.threat_description || 'Threat Activity'}</td>
                          <td>
                            <span className="orange-badge-subtle">Detected</span>
                          </td>
                        </tr>

                        <tr>
                          <td>
                            <div className="d-flex align-items-center">
                              <div className="flex-shrink-0 avatar-xs">
                                <div className="avatar-title orange-avatar-bg rounded-circle">
                                  <i className="ri-server-line"></i>
                                </div>
                              </div>
                              <div className="flex-grow-1 ms-2">
                                Agent
                              </div>
                            </div>
                          </td>
                          <td>{threat.agent_name || 'N/A'}</td>
                          <td>Monitoring Agent</td>
                          <td>
                            <span className={getCriticalityBadgeClass(threat.asset_criticality)}>
                              {threat.asset_criticality || 'Low'}
                            </span>
                          </td>
                        </tr>

                        <tr>
                          <td>
                            <div className="d-flex align-items-center">
                              <div className="flex-shrink-0 avatar-xs">
                                <div className="avatar-title orange-avatar-bg rounded-circle">
                                  <i className="ri-time-line"></i>
                                </div>
                              </div>
                              <div className="flex-grow-1 ms-2">
                                Status
                              </div>
                            </div>
                          </td>
                          <td>{threat.status || 'Active'}</td>
                          <td>Current Status</td>
                          <td>
                            <span className="orange-badge-subtle">Active</span>
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </TabPane>

        {/* TAB 2: Security (Compliance & MITRE) */}
        <TabPane tabId="2">
          <Row>
            <Col xxl={6}>
              <Card style={detailCardShell} className="card-height-100 ">
                <CardHeader className="align-items-center d-flex orange-card-header">
                  <h4 className="card-title mb-0 flex-grow-1 text-white">
                    <i className="ri-shield-check-line me-2"></i>
                    Compliance Frameworks
                  </h4>
                </CardHeader>
                <CardBody>
                  {loading ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : complianceData && complianceData.length > 0 ? (
                    <div className="table-responsive table-card">
                      <Table className="table-borderless table-centered align-middle table-nowrap mb-0">
                        <thead className="table-light">
                          <tr>
                            <th scope="col">Framework</th>
                            <th scope="col">Control ID</th>
                            <th scope="col">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {complianceData.map((c, i) => (
                            <tr key={i}>
                              <td>
                                <span className="orange-badge-subtle">
                                  {c.framework}
                                </span>
                              </td>
                              <td><code className="text-muted">{c.control_id}</code></td>
                              <td>
                                <span className="orange-badge-subtle">
                                  {threat.status || 'Active'}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  ) : (
                    <div className="text-center py-5">
                      <i className="ri-information-line display-4 text-muted mb-3 d-block"></i>
                      <p className="text-muted">No compliance mappings available</p>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>

            <Col xxl={6}>
              <Card style={detailCardShell} className="card-height-100 ">
                <CardHeader className="align-items-center d-flex orange-card-header-dark">
                  <h4 className="card-title mb-0 flex-grow-1 text-white">
                    <i className="ri-skull-2-line me-2"></i>
                    MITRE ATT&CK
                  </h4>
                </CardHeader>
                <CardBody>
                  {loading ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : mitreData && mitreData.length > 0 ? (
                    <div className="table-responsive table-card">
                      <Table className="table-borderless table-centered align-middle table-nowrap mb-0">
                        <thead className="table-light">
                          <tr>
                            <th scope="col">Tactic</th>
                            <th scope="col">Technique</th>
                            <th scope="col">ID</th>
                          </tr>
                        </thead>
                        <tbody>
                          {mitreData.map((m, i) => (
                            <tr key={i}>
                              <td>
                                <span className="orange-badge-subtle">
                                  {m.tactic}
                                </span>
                              </td>
                              <td>{m.technique}</td>
                              <td><code className="text-muted">{m.technique_id}</code></td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  ) : (
                    <div className="text-center py-5">
                      <i className="ri-information-line display-4 text-muted mb-3 d-block"></i>
                      <p className="text-muted">No MITRE ATT&CK mappings available</p>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Security Rule Details */}
          <Row className="mt-3">
            <Col xl={12}>
              <Card style={detailCardShell}>
                <CardHeader className="align-items-center d-flex orange-card-header-darker">
                  <h4 className="card-title mb-0 flex-grow-1 text-white">
                    <i className="ri-file-shield-line me-2"></i>
                    Security Rule Information
                  </h4>
                </CardHeader>
                <CardBody>
                  <Row className="g-3">
                    <Col md={6}>
                      <div className="d-flex align-items-center border-end pb-3">
                        <div className="flex-shrink-0">
                          <div className="avatar-sm">
                            <div className="avatar-title orange-avatar-bg rounded-3 fs-18">
                              <i className="ri-hashtag"></i>
                            </div>
                          </div>
                        </div>
                        <div className="flex-grow-1 ms-3">
                          <p className="text-muted mb-1">Rule ID</p>
                          <h5 className="mb-0">{threat.wazuh_rule_id}</h5>
                        </div>
                      </div>
                    </Col>

                    <Col md={6}>
                      <div className="d-flex align-items-center pb-3">
                        <div className="flex-shrink-0">
                          <div className="avatar-sm">
                            <div className="avatar-title orange-avatar-bg rounded-3 fs-18">
                              <i className="ri-bar-chart-line"></i>
                            </div>
                          </div>
                        </div>
                        <div className="flex-grow-1 ms-3">
                          <p className="text-muted mb-1">Severity Level</p>
                          <h5 className="mb-0">
                            <span className="orange-badge-subtle">
                              Level {threat.wazuh_rule_level}
                            </span>
                          </h5>
                        </div>
                      </div>
                    </Col>

                    <Col xs={12}>
                      <div className="border-top pt-3">
                        <p className="text-muted mb-2">Description:</p>
                        <p className="mb-0">{threat.wazuh_rule_description || 'N/A'}</p>
                      </div>
                    </Col>

                    {threat.wazuh_rule_groups && threat.wazuh_rule_groups.length > 0 && (
                      <Col xs={12}>
                        <div className="border-top pt-3">
                          <p className="text-muted mb-2">Rule Groups:</p>
                          <div className="d-flex flex-wrap gap-2">
                            {threat.wazuh_rule_groups.map((group, idx) => (
                              <span key={idx} className="orange-badge-subtle">
                                {group}
                              </span>
                            ))}
                          </div>
                        </div>
                      </Col>
                    )}
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </TabPane>

        {/* TAB 3: Technical Details */}
        <TabPane tabId="3">
          <Row className="g-3">
            {/* System Information */}
            <Col xxl={6}>
              <Card style={detailCardShell} className="card-height-100 ">
                <CardHeader className="orange-card-header">
                  <h5 className="card-title mb-0 text-white">
                    <i className="ri-computer-line me-2"></i>
                    System Information
                  </h5>
                </CardHeader>
                <CardBody>
                  <div className="table-responsive">
                    <Table className="table-borderless mb-0">
                      <tbody>
                        <tr>
                          <td className="text-muted" width="45%">Threat ID:</td>
                          <td><code>{threat.threat_id}</code></td>
                        </tr>
                        <tr>
                          <td className="text-muted">Agent Name:</td>
                          <td>{threat.agent_name}</td>
                        </tr>
                        <tr>
                          <td className="text-muted">Asset Name:</td>
                          <td>{threat.asset_name}</td>
                        </tr>
                        <tr>
                          <td className="text-muted">Threat Type:</td>
                          <td>
                            <span className="orange-badge-subtle">
                              {threat.threat_type}
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <td className="text-muted">Criticality:</td>
                          <td>
                            <span className={getCriticalityBadgeClass(threat.asset_criticality)}>
                              {threat.asset_criticality}
                            </span>
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  </div>
                </CardBody>
              </Card>
            </Col>

            {/* Network Information */}
            <Col xxl={6}>
              <Card style={detailCardShell} className="card-height-100 ">
                <CardHeader className="orange-card-header-dark">
                  <h5 className="card-title mb-0 text-white">
                    <i className="ri-wifi-line me-2"></i>
                    Network Information
                  </h5>
                </CardHeader>
                <CardBody>
                  <div className="table-responsive">
                    <Table className="table-borderless mb-0">
                      <tbody>
                        <tr>
                          <td className="text-muted" width="45%">Source IP:</td>
                          <td><code>{threat.source_ip || 'N/A'}</code></td>
                        </tr>
                        <tr>
                          <td className="text-muted">Destination IP:</td>
                          <td><code>{threat.destination_ip || 'N/A'}</code></td>
                        </tr>
                        <tr>
                          <td className="text-muted">Status:</td>
                          <td>
                            <span className="orange-badge-subtle">
                              {threat.status || 'Active'}
                            </span>
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Compliance Information */}
          <Row className="mt-3">
            <Col xl={12}>
              <Card style={detailCardShell}>
                <CardHeader className="orange-card-header-darker">
                  <h5 className="card-title mb-0 text-white">
                    <i className="ri-shield-check-line me-2"></i>
                    Compliance Information
                  </h5>
                </CardHeader>
                <CardBody>
                  <Row className="g-3">
                    <Col md={6}>
                      <div className="border-end">
                        <p className="text-muted mb-2">Compliance Frameworks:</p>
                        <div className="d-flex flex-wrap gap-2">
                          {threat.compliance_frameworks && threat.compliance_frameworks.length > 0 ? (
                            threat.compliance_frameworks.map((framework, idx) => (
                              <span key={idx} className="orange-badge-subtle">
                                {framework}
                              </span>
                            ))
                          ) : (
                            <span className="text-muted">No frameworks</span>
                          )}
                        </div>
                      </div>
                    </Col>

                    <Col md={6}>
                      <p className="text-muted mb-2">Compliance Controls:</p>
                      <div className="d-flex flex-wrap gap-2">
                        {threat.compliance_controls && threat.compliance_controls.length > 0 ? (
                          threat.compliance_controls.map((control, idx) => (
                            <span key={idx} className="orange-badge-subtle">
                              {control}
                            </span>
                          ))
                        ) : (
                          <span className="text-muted">No controls</span>
                        )}
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Timestamp */}
          <Row className="mt-3">
            <Col xl={12}>
              <Card style={detailCardShell}>
                <CardHeader className="orange-card-header">
                  <h5 className="card-title mb-0 text-white">
                    <i className="ri-time-line me-2"></i>
                    Timestamp Information
                  </h5>
                </CardHeader>
                <CardBody>
                  <Row className="g-3">
                    <Col md={4}>
                      <p className="text-muted mb-1">Threat Detected:</p>
                      <h6 className="mb-0">{threat.timestamp ? new Date(threat.timestamp).toLocaleString() : 'N/A'}</h6>
                    </Col>
                    <Col md={4}>
                      <p className="text-muted mb-1">Created At:</p>
                      <h6 className="mb-0">{threat.created_at ? new Date(threat.created_at).toLocaleString() : 'N/A'}</h6>
                    </Col>
                    <Col md={4}>
                      <p className="text-muted mb-1">Updated At:</p>
                      <h6 className="mb-0">{threat.updated_at ? new Date(threat.updated_at).toLocaleString() : 'N/A'}</h6>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </TabPane>

        {/* TAB 4: Raw Data */}
<TabPane tabId="4">
  <Row>
    <Col xl={12}>
      <Card style={detailCardShell}>
        <CardHeader className="align-items-center d-flex orange-card-header">
          <h4 className="card-title mb-0 flex-grow-1 text-white">
            <i className="ri-code-line me-2"></i>
            Complete Threat Object (JSON)
          </h4>
          <div className="flex-shrink-0">
            <div className="btn-group btn-group-sm">
              <button
                className="btn btn-sm orange-button"
                onClick={(e) => {
                  const jsonString = JSON.stringify(getSanitizedThreat(threat), null, 2);
                  navigator.clipboard.writeText(jsonString);
                  
                  const btn = e.currentTarget;
                  const originalText = btn.innerHTML;
                  btn.innerHTML = '<i class="ri-check-line me-1"></i>Copied!';
                  btn.disabled = true;
                  
                  setTimeout(() => {
                    btn.innerHTML = originalText;
                    btn.disabled = false;
                  }, 2000);
                }}
              >
                <i className="ri-file-copy-line me-1"></i>
                Copy
              </button>
              <button
                className="btn btn-sm orange-button ms-1"
                onClick={() => {
                  const jsonString = JSON.stringify(getSanitizedThreat(threat), null, 2);
                  const blob = new Blob([jsonString], { type: 'application/json' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `threat-${threat.threat_id}.json`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                }}
              >
                <i className="ri-download-line me-1"></i>
                Download
              </button>
            </div>
          </div>
        </CardHeader>
        <CardBody>
          <div 
            className="border rounded bg-light"
            style={{ 
              maxHeight: '600px', 
              overflowY: 'auto'
            }}
          >
            <pre 
              className="mb-0 p-3"
              style={{
                fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                fontSize: '0.85rem',
                lineHeight: '1.6',
                margin: 0
              }}
            >
              {JSON.stringify(getSanitizedThreat(threat), null, 2)}
            </pre>
          </div>

          {/* JSON Stats */}
          <div className="mt-3 pt-3 border-top">
            <Row className="text-center g-3">
              <Col xs={4}>
                <div className="p-2">
                  <h5 className="mb-1 orange-text">{Object.keys(getSanitizedThreat(threat) || {}).length}</h5>
                  <p className="text-muted mb-0 fs-12">Total Keys</p>
                </div>
              </Col>
              <Col xs={4}>
                <div className="p-2 border-start border-end">
                  <h5 className="mb-1 orange-text">
                    {(JSON.stringify(getSanitizedThreat(threat)).length / 1024).toFixed(2)} KB
                  </h5>
                  <p className="text-muted mb-0 fs-12">JSON Size</p>
                </div>
              </Col>
              <Col xs={4}>
                <div className="p-2">
                  <h5 className="mb-1">
                    <code className="small orange-text">{threat.threat_id?.split('-').pop()}</code>
                  </h5>
                  <p className="text-muted mb-0 fs-12">Short ID</p>
                </div>
              </Col>
            </Row>
          </div>
        </CardBody>
      </Card>
    </Col>
  </Row>
</TabPane>
      </TabContent>
    </>
  );
}

export default Office365Details;
